// pages/Community/SkoolOnboarding.jsx
//
// The new-member onboarding ladder, per member: which rungs went out (welcome
// → reminder → warning → final), what they still had not posted, and — the
// part that matters since 2026-09-26 — their prospect state and whether the
// ladder is STOPPED for them. It only continues for a member who is still
// 'new' and has not introduced themselves or replied; anyone engaged, booked,
// close to converting, converted or not interested hears no participation
// warnings (worker services/prospectClassifier).
import React, { useEffect, useState, useCallback, useMemo } from "react";
import addNotification from "helpers/notify";
import { getSkoolOnboarding, setSkoolProspectState } from "api/connectors/connectors";
import { COLUMNS as PROSPECT_STATES } from "./SkoolProspects";

const PROSPECT = Object.fromEntries(PROSPECT_STATES.map((c) => [c.key, c]));
const SOURCE_LABEL = { manual: "set by you", booking: "booking", rule: "rule", ai: "AI" };
const RUNGS = [
  { key: "welcome", label: "Welcome" }, { key: "reminder", label: "Reminder" },
  { key: "warning", label: "Warning" }, { key: "final", label: "Final" },
];
const STATUS_COLOR = { success: "#1a7f37", queued: "#9a6700", running: "#9a6700", held: "#0b3d91", failed: "#b42318", cancelled: "#98a2b3" };
const MISSING = { intro: "introduction", working: "what they're building", help: "what they're stuck on" };
const fmt = (d) => { try { return d ? new Date(d).toLocaleString() : "—"; } catch (_) { return "—"; } };
const stopLabel = (s) => (!s ? "" : s === "introduced" ? "Stopped: introduced" : `Stopped: ${(PROSPECT[s.replace("prospect:", "")] || {}).label || s}`);

export default function SkoolOnboarding({ brandId }) {
  const [data, setData] = useState({ config: null, members: [] });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState("");
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSkoolOnboarding(brandId ? { brandId } : {});
      setData({ config: (res && res.config) || null, members: (res && res.members) || [] });
    } catch (e) {
      addNotification(e?.message || e?.error || "Could not load onboarding", "error");
    } finally { setLoading(false); }
  }, [brandId]);
  useEffect(() => { load(); }, [load]);

  const rows = data.members;
  const shown = useMemo(() => {
    if (!filter) return rows;
    if (filter === "running") return rows.filter((m) => m.phase === "running");
    if (filter === "stopped") return rows.filter((m) => m.phase === "stopped");
    if (filter === "finished") return rows.filter((m) => m.phase === "finished");
    if (filter === "closed") return rows.filter((m) => m.phase === "closed");
    if (filter === "final") return rows.filter((m) => m.delivered.includes("final"));
    return rows.filter((m) => m.prospect && m.prospect.state === filter);
  }, [rows, filter]);

  const changeState = async (p, state) => {
    if (!p || !state) return;
    try {
      await setSkoolProspectState(p._id, state, state === "auto" ? "" : "set from Skool onboarding");
      addNotification(state === "auto" ? "Handed back to the classifier" : `Now: ${(PROSPECT[state] || {}).label || state}`, "success");
      load();
    } catch (e) { addNotification(e?.error || e?.message || "Could not change the state", "error"); }
  };

  const c = data.config;
  const count = (ph) => rows.filter((m) => m.phase === ph).length;
  return (
    <div>
      <div className="d-flex align-items-center mb-2" style={{ gap: 10, flexWrap: "wrap" }}>
        <h6 className="mb-0">Skool onboarding</h6>
        <span className="text-muted small" data-testid="onboarding-summary">
          {rows.length} members on the ladder · {count("running")} running · {count("stopped")} stopped · {count("finished")} finished — due for your review · {count("closed")} window closed
        </span>
        <select className="form-control form-control-sm ml-auto" style={{ width: 210 }} aria-label="Onboarding filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All members</option>
          <option value="running">Ladder still running</option>
          <option value="stopped">Ladder stopped</option>
          <option value="finished">Finished — due for review</option>
          <option value="closed">Window closed, no final notice</option>
          <option value="final">Got the final notice</option>
          {PROSPECT_STATES.map((x) => <option key={x.key} value={x.key}>Now: {x.label}</option>)}
        </select>
        <button className="btn btn-link btn-sm p-0" onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>
      </div>
      {c && (
        <div className="text-muted small mb-2" data-testid="onboarding-config">
          Ladder {c.enabled ? "ON" : "OFF"} · email twin {c.emailEnabled ? "on" : "off"} · review gate {c.reviewGate ? "on" : "off"}
          {c.startAt ? ` · members who joined from ${fmt(c.startAt)}` : " · no start date (nobody enrolled)"}
        </div>
      )}

      {!shown.length && !loading && <div className="text-muted small">{rows.length ? "Nothing matches this filter." : "Nobody has been on the onboarding ladder for this brand."}</div>}

      {shown.map((m) => {
        const p = m.prospect;
        const pc = p ? (PROSPECT[p.state] || PROSPECT.new) : null;
        const isOpen = open === `${m.brand_id}|${m.userId}`;
        const byRung = Object.fromEntries(m.rungs.map((r) => [r.rung, r]));
        return (
          <div key={`${m.brand_id}|${m.userId}`} data-testid={`onb-${m.userId}`} style={{ border: "1px solid #e4e7ec", borderRadius: 8, padding: "8px 10px", marginBottom: 8, background: "#fff" }}>
            <div className="d-flex align-items-center" style={{ gap: 8, flexWrap: "wrap", cursor: "pointer" }} onClick={() => setOpen(isOpen ? "" : `${m.brand_id}|${m.userId}`)}>
              <b className="small">{m.name || m.handle || m.userId}</b>
              {pc && <span data-testid="onboarding-prospect-pill" title={`${pc.label} (${SOURCE_LABEL[p.stateSource] || p.stateSource})${p.reason ? ` — ${p.reason}` : ""}`}
                style={{ fontSize: "0.68rem", fontWeight: 600, color: pc.color, background: pc.bg, borderRadius: 10, padding: "1px 8px" }}>{pc.label}{p.stateSource === "manual" ? " ✎" : ""}</span>}
              {/* The ladder rungs as a strip: filled = delivered, outlined = attempted, grey = not reached. */}
              <span className="d-flex" style={{ gap: 3 }}>
                {RUNGS.map((r) => {
                  const x = byRung[r.key];
                  const col = x ? (STATUS_COLOR[x.status] || "#98a2b3") : "#d0d5dd";
                  return <span key={r.key} data-testid={`rung-${r.key}`} title={x ? `${r.label}: ${x.status} ${fmt(x.at)}${x.error ? ` — ${x.error}` : ""}` : `${r.label}: not reached`}
                    style={{ fontSize: "0.62rem", fontWeight: 600, padding: "1px 6px", borderRadius: 6, color: x && x.status === "success" ? "#fff" : col, background: x && x.status === "success" ? col : "transparent", border: `1px solid ${col}` }}>{r.label}</span>;
                })}
              </span>
              <span className="small" data-testid="onboarding-stop"
                title={m.phase === "finished" ? "The final notice went out; nothing more is sent. Removal is a manual decision."
                  : m.phase === "closed" ? "Aged out of the ladder (72h window + 24h) before a final notice went out; nothing more is sent." : ""}
                style={{ color: m.phase === "stopped" ? "#1a7f37" : m.phase === "finished" ? "#b42318" : m.phase === "closed" ? "#667085" : "#9a6700" }}>
                {m.phase === "stopped" ? stopLabel(m.stoppedBy) : m.phase === "finished" ? "Finished — due for review" : m.phase === "closed" ? "Window closed" : "Ladder running"}
              </span>
              <span className="text-muted small ml-auto">{fmt(m.lastAt)}</span>
            </div>
            {m.missing && m.phase !== "stopped" && (
              <div className="small text-muted" style={{ marginTop: 3 }}>Still missing at the last rung: {m.missing.split(",").map((k) => MISSING[k] || k).join(", ")}</div>
            )}
            {isOpen && (
              <div style={{ marginTop: 6 }}>
                {m.rungs.map((r, i) => (
                  <div key={i} className="small" style={{ borderLeft: `3px solid ${STATUS_COLOR[r.status] || "#98a2b3"}`, paddingLeft: 8, marginBottom: 6 }}>
                    <b>{(RUNGS.find((x) => x.key === r.rung) || {}).label || r.rung}</b> · {r.status} · {fmt(r.at)}
                    {r.error ? <span style={{ color: "#b42318" }}> — {r.error}</span> : null}
                    <div style={{ whiteSpace: "pre-wrap", color: "#475467" }}>{r.text}</div>
                  </div>
                ))}
                {m.handle && <a className="small" href={`https://www.skool.com/@${m.handle}`} target="_blank" rel="noreferrer">@{m.handle}</a>}
                {pc ? (
                  <div data-testid="onboarding-prospect-block" style={{ marginTop: 6, padding: 8, borderRadius: 8, background: pc.bg, fontSize: "0.78rem" }}>
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                      <b style={{ color: pc.color }}>{pc.label}</b>
                      <span className="text-muted">· {SOURCE_LABEL[p.stateSource] || p.stateSource}{p.introduced ? " · introduced" : ""}</span>
                      <select className="form-control form-control-sm ml-auto" style={{ width: 180 }} aria-label="Change prospect state" value=""
                        onChange={(e) => changeState(p, e.target.value)}>
                        <option value="">Change state…</option>
                        {PROSPECT_STATES.filter((x) => x.key !== p.state || p.stateSource !== "manual").map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                        {p.stateSource === "manual" && <option value="auto">Back to automatic</option>}
                      </select>
                    </div>
                    {p.reason && <div style={{ color: "#475467" }}>{p.reason}</div>}
                    {p.bookingAt && <div style={{ color: "#6a3fb5" }}>Call booked: {fmt(p.bookingAt)}</div>}
                    <div className="text-muted">{pc.hint}</div>
                  </div>
                ) : <div className="text-muted small" style={{ marginTop: 6 }}>Not classified yet.</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
